
import { useMemo } from "react";
import { Part, PartCategory, PartSubcategory, PartFilter } from "@/data/parts";
import { SortOption } from "./filter-types";

/**
 * Hook with utility functions for filtering parts
 */
export const usePartsFilterUtils = () => {
  /**
   * Filters and sorts parts based on provided criteria
   */
  const getFilteredParts = (
    allParts: Part[],
    searchQuery: string,
    selectedCategory: PartCategory | "all",
    selectedSubcategory: PartSubcategory | "all",
    activeFilters: PartFilter[],
    sortBy: SortOption,
    minStarRating: number
  ): Part[] => {
    // Filter parts based on criteria
    let filtered = allParts.filter(part => {
      // Filter by search query
      const matchesSearch = 
        searchQuery === "" || 
        part.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        part.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filter by category
      const matchesCategory = selectedCategory === "all" || part.category === selectedCategory;
      
      // Filter by subcategory
      const matchesSubcategory = selectedSubcategory === "all" || part.subcategory === selectedSubcategory;
      
      // Filter by special filters - include parts that match the active filters
      const specialFiltersOk = activeFilters.filter(f => 
        !["requires-root", "wiring", "requires-printing", "requires-tools", "requires-purchase"].includes(f)
      ).every(filter => filter === "upgrade" ? part.filters.includes(filter) : true);
      
      // Requirement filters - HIDE parts that have the requirement when the filter is active
      const rootOk = !activeFilters.includes("requires-root") || !part.requiresRoot;
      const wiringOk = !activeFilters.includes("wiring") || !part.requiresWiring;
      const printingOk = !activeFilters.includes("requires-printing") || !(part.requiresPrinting || !!part.printedParts);
      const toolsOk = !activeFilters.includes("requires-tools") || !(part.requiresTools || part.subcategory === "tools");
      const purchaseOk = !activeFilters.includes("requires-purchase") || !(part.requiresPurchase || !!(part.amazonLink || part.aliExpressLink));
      
      // Filter by minimum rating
      const matchesRating = part.rating >= minStarRating;
      
      return matchesSearch && 
             matchesCategory && 
             matchesSubcategory && 
             specialFiltersOk && 
             rootOk && 
             wiringOk && 
             printingOk && 
             toolsOk && 
             purchaseOk && 
             matchesRating;
    });

    // Sort by the selected option
    switch (sortBy) {
      case "rating-high":
        filtered = [...filtered].sort((a, b) => b.rating - a.rating);
        break;
      case "rating-low":
        filtered = [...filtered].sort((a, b) => a.rating - b.rating);
        break;
    }

    return filtered;
  };

  return { getFilteredParts };
};
